import * as Checkbox from '@radix-ui/react-checkbox';
import {
  Check,
  CircleDot,
  Edit3,
  Layers3,
  PencilLine,
  Plus,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '../../lib/cn';
import { usePagination } from '../../hooks/usePagination';
import { formatRecurrence } from '../../lib/recurrence';
import Pagination from '../Pagination';
import CollectionIcon, { getCollectionIconMeta } from '../ui/CollectionIcon';
import { BearySprite } from '../ui/BearySprite';
import RecurrenceEditor from '../ui/RecurrenceEditor';
import SegmentedTabs from '../ui/SegmentedTabs';

const editorIconChoices = [
  'cart',
  'house',
  'briefcase',
  'heart',
  'airplane',
  'book',
  'gift',
  'graduationcap',
  'list.bullet',
];

const editorColors = ['#0f766e', '#60a5fa', '#99d2ce', '#fb923c', '#e5e7eb'];

const inputClassName =
  'w-full rounded-xl border border-app-border/80 bg-app-surface px-4 py-3 text-sm text-app-ink outline-none transition placeholder:text-app-muted/70 focus:border-app-accent/40 focus:ring-2 focus:ring-app-accent/20';

const sectionClassName =
  'rounded-[22px] border border-app-border/70 bg-app-surface/95 p-6 shadow-subtle lg:p-7';

function scopeLabel(scope) {
  return scope === 'family' ? 'Family shared' : 'Personal';
}

function statusLabel(status) {
  return toDisplayText(status, 'open').replace(/-/g, ' ');
}

function formatDate(value) {
  if (!value) {
    return 'No date';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function toDisplayText(value, fallback = '') {
  if (value == null || value === '') {
    return fallback;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const parts = value.map((entry) => toDisplayText(entry, '')).filter(Boolean);
    return parts.length ? parts.join(', ') : fallback;
  }

  if (typeof value === 'object') {
    const preferredKeys = [
      'title',
      'name',
      'reason',
      'description',
      'details',
      'info',
      'destination',
      'category',
      'status',
      'id',
    ];

    for (const key of preferredKeys) {
      const nextValue = value?.[key];
      if (
        typeof nextValue === 'string' ||
        typeof nextValue === 'number' ||
        typeof nextValue === 'boolean'
      ) {
        return String(nextValue);
      }
    }

    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }

  return fallback;
}

function getListId(list) {
  return list?._id || list?.id || '';
}

function getItemId(item) {
  return item?._id || item?.id || item?.itemId || '';
}

function getListCount(list) {
  if (Array.isArray(list?.items)) {
    return list.items.length;
  }

  return list?.counts?.open ?? list?.counts?.total ?? list?.counts?.all ?? 0;
}

function toneForStatus(type) {
  return {
    idle: 'border-slate-200 bg-slate-50 text-slate-700',
    loading: 'border-amber-200 bg-amber-50 text-amber-800',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    error: 'border-red-200 bg-red-50 text-red-800',
  }[type] || 'border-slate-200 bg-slate-50 text-slate-700';
}

function toneForPriority(priority) {
  return {
    urgent: 'border-red-200 bg-red-50 text-red-700',
    high: 'border-orange-200 bg-orange-50 text-orange-700',
    medium: 'border-amber-200 bg-amber-50 text-amber-700',
    low: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  }[priority] || 'border-slate-200 bg-slate-50 text-slate-700';
}

function toneForItemStatus(status) {
  return {
    open: 'border-slate-200 bg-slate-50 text-slate-700',
    completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    cancelled: 'border-red-200 bg-red-50 text-red-700',
    'in progress': 'border-blue-200 bg-blue-50 text-blue-700',
    'in-progress': 'border-blue-200 bg-blue-50 text-blue-700',
  }[status] || 'border-slate-200 bg-slate-50 text-slate-700';
}

function ScopeBadge({ scope }) {
  const isFamily = scope === 'family';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
        isFamily
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-blue-200 bg-blue-50 text-blue-700'
      )}
    >
      {isFamily ? <Users className="h-3.5 w-3.5" aria-hidden="true" /> : <CircleDot className="h-3.5 w-3.5" aria-hidden="true" />}
      <span>{scopeLabel(scope)}</span>
    </span>
  );
}

function StatusPill({ children, tone }) {
  const isLoading = typeof tone === 'string' && tone.includes('bg-amber-50');

  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold capitalize', tone)}>
      {isLoading ? (
        <BearySprite preset="loading" size={28} label="Beary is helping load this" />
      ) : null}
      {children}
    </span>
  );
}

function SectionTitle({ title, copy, action }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="m-0 text-lg font-semibold text-app-ink">{title}</h2>
        {copy ? <p className="mt-2 text-sm leading-7 text-app-muted">{copy}</p> : null}
      </div>
      {action}
    </div>
  );
}

function MetricCard({ label, value, accentClassName }) {
  return (
    <div className="rounded-2xl border border-app-border/70 bg-app-surface/85 p-5 shadow-subtle">
      <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">{label}</p>
      <p className={cn('mt-2 text-3xl font-semibold tracking-tight text-app-ink', accentClassName)}>{value}</p>
    </div>
  );
}

function CollectionHeroCard({ currentUser, scopeFilter, onScopeFilterChange, personalCount, familyCount, visibleCount }) {
  return (
    <section className={cn(sectionClassName, 'bg-app-shell overflow-hidden')}>
      <div className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.95fr)]">
        <div className="space-y-7">
          <div className="space-y-4">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-app-accent-strong">
              Collections
            </p>
            <h1 className="m-0 text-4xl font-semibold tracking-tight text-app-ink">
              {currentUser?.name ? `${currentUser.name}'s collections` : 'My Collections'}
            </h1>
            <p className="max-w-2xl text-base leading-7 text-app-muted">
              Personal and family shared lists in one place. Keep the same workflows, but
              move them into a cleaner dashboard surface that is easier to scan.
            </p>
          </div>

          <SegmentedTabs
            value={scopeFilter}
            onValueChange={onScopeFilterChange}
            ariaLabel="Collections scope filter"
            items={[
              { value: 'all', label: 'All' },
              { value: 'personal', label: 'Personal' },
              { value: 'family', label: 'Family' },
            ]}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-1">
          <MetricCard label="Personal" value={personalCount} accentClassName="text-blue-700" />
          <MetricCard label="Family" value={familyCount} accentClassName="text-emerald-700" />
          <MetricCard label="Visible" value={visibleCount} accentClassName="text-app-accent-strong" />
        </div>
      </div>
    </section>
  );
}

function ListCard({ list, scope, onOpen }) {
  const listId = getListId(list);
  const resolvedScope = scope || list?.scope || 'personal';
  const count = getListCount(list);
  const listName = toDisplayText(list?.name ?? list?.title, 'Untitled list');
  const listDescription = toDisplayText(
    list?.description ?? list?.summary ?? list?.notes,
    resolvedScope === 'family'
      ? 'Shared with the household.'
      : 'Kept in your personal workspace.'
  );
  const updatedText = formatDate(
    list?.updatedAt || list?.modifiedAt || list?.createdAt || list?.lastUpdatedAt
  );
  const iconMeta = getCollectionIconMeta(list?.icon);

  return (
    <button
      className="group flex h-full flex-col rounded-[24px] border border-app-border/80 bg-app-surface/95 p-6 text-left shadow-subtle transition hover:-translate-y-1 hover:border-app-accent/25 hover:shadow-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-accent/25 lg:p-7"
      type="button"
      onClick={() => onOpen(resolvedScope, listId)}
      aria-label={`${listName} ${count ?? 0} items`}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-subtle"
          style={{ backgroundColor: list?.color || '#60a5fa' }}
        >
          <CollectionIcon icon={list?.icon} size="lg" />
        </div>
        <ScopeBadge scope={resolvedScope} />
      </div>

      <div className="mt-6 space-y-3">
        <p className="m-0 text-xl font-semibold tracking-tight text-app-ink">{listName}</p>
        <p className="m-0 text-sm leading-6 text-app-muted">{listDescription}</p>
      </div>

      <div className="mt-7 flex items-center justify-between gap-4 border-t border-app-border/60 pt-5">
        <div>
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-app-muted">
            {iconMeta.label}
          </p>
          <p className="mt-1 text-sm font-medium text-app-ink">{count ?? 0} items</p>
        </div>
        <p className="m-0 text-sm text-app-muted">Updated {updatedText}</p>
      </div>
    </button>
  );
}

function ItemRow({ item, isEditing, onToggleStatus, onEdit, onDelete }) {
  const itemId = getItemId(item);
  const isCompleted = item?.status === 'completed';
  const itemTitle = toDisplayText(item?.title ?? item?.name, 'Untitled item');
  const itemNotes = toDisplayText(item?.notes ?? item?.description, 'No notes yet.');
  const priority = toDisplayText(item?.priority, 'medium').toLowerCase();
  const status = statusLabel(item?.status);
  const recurrenceLabel = formatRecurrence(item?.recurrence);

  return (
    <article
      className={cn(
        'flex gap-5 rounded-[22px] border p-5 shadow-subtle transition lg:p-6',
        isEditing
          ? 'border-app-accent/30 bg-blue-50/70'
          : 'border-app-border/80 bg-app-surface/95'
      )}
      key={itemId || itemTitle}
    >
      <Checkbox.Root
        checked={isCompleted}
        onCheckedChange={() => onToggleStatus(item)}
        className={cn(
          'mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition focus:outline-none focus:ring-2 focus:ring-app-accent/25',
          isCompleted
            ? 'border-emerald-600 bg-emerald-600 text-white'
            : 'border-app-border bg-app-surface text-transparent hover:border-emerald-500'
        )}
        aria-label={isCompleted ? 'Reopen item' : 'Complete item'}
      >
        <Checkbox.Indicator>
          <Check className="h-4 w-4" aria-hidden="true" />
        </Checkbox.Indicator>
      </Checkbox.Root>

      <div className="min-w-0 flex-1">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p
              className={cn(
                'm-0 text-base font-semibold text-app-ink',
                isCompleted && 'text-app-muted line-through'
              )}
            >
              {itemTitle}
            </p>
            <p className="mt-1 text-sm leading-6 text-app-muted">{itemNotes}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={toneForItemStatus(status.toLowerCase())}>{status}</StatusPill>
            <StatusPill tone={toneForPriority(priority)}>{priority}</StatusPill>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-4 text-sm text-app-muted">
            <span>Due {formatDate(item?.dueDate)}</span>
            {recurrenceLabel ? <span>Repeats {recurrenceLabel}</span> : null}
            {item?.assignee ? <span>Assigned to {toDisplayText(item.assignee)}</span> : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
              type="button"
              onClick={() => onEdit(item)}
            >
              <PencilLine className="h-4 w-4" aria-hidden="true" />
              <span>{isEditing ? 'Editing' : 'Edit'}</span>
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
              type="button"
              onClick={() => onDelete(itemId)}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function EmptyState({ title, copy }) {
  return (
    <div className="rounded-[22px] border border-dashed border-app-border bg-app-surface/70 px-6 py-12 text-center shadow-subtle lg:py-14">
      <div className="mb-4 flex justify-center">
        <BearySprite preset="wave" size={86} label="Beary family guide" />
      </div>
      <p className="m-0 text-lg font-semibold text-app-ink">{title}</p>
      <p className="mt-3 text-sm leading-7 text-app-muted">{copy}</p>
    </div>
  );
}

function ListsView({
  currentUser,
  mode,
  listsStatus,
  listEditorStatus,
  family,
  allLists,
  scopeFilter,
  onScopeFilterChange,
  activeScope,
  activeList,
  activeListItems,
  activeFilter,
  editingListItemId,
  listItemDraft,
  listEditorForm,
  onOpenList,
  onOpenListEditor,
  onListFilterChange,
  onListItemDraftChange,
  onAddListItem,
  onToggleListItemStatus,
  onEditListItem,
  onDeleteListItem,
  onCancelListItemEdit,
  onEditorChange,
  onSaveList,
  onArchiveList,
  onDeleteList,
  onBackToCollections,
}) {
  const itemsPagination = usePagination(activeListItems, 10);
  const allListsPagination = usePagination(allLists, 12);
  const resetItemsPage = itemsPagination.goToFirstPage;
  const resetAllListsPage = allListsPagination.goToFirstPage;

  useEffect(() => {
    resetItemsPage();
  }, [activeListItems, resetItemsPage]);

  useEffect(() => {
    resetAllListsPage();
  }, [allLists, resetAllListsPage]);

  if (mode === 'editor') {
    const isFamilyScope = listEditorForm.scope === 'family';
    const isEditing = Boolean(listEditorForm.listId);
    const previewName = listEditorForm.name?.trim() || 'Untitled collection';

    return (
      <section className="w-full max-w-[1380px] text-app-ink">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-8">
            <section className={sectionClassName}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-app-accent-strong">
                    Collections
                  </p>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-app-ink">
                    {isEditing ? 'Edit collection' : 'Create collection'}
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-app-muted">
                    Keep the behavior unchanged, but move the editor onto the new surface system.
                  </p>
                </div>
                <button
                  className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-2.5 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                  type="button"
                  onClick={onBackToCollections}
                >
                  Close
                </button>
              </div>
            </section>

            <section className={sectionClassName}>
              <SectionTitle
                title="Basics"
                copy="Set the name, icon, and color palette for this collection."
              />
              <div className="mt-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="editor-list-name">
                    List name
                  </label>
                  <input
                    id="editor-list-name"
                    value={listEditorForm.name}
                    onChange={(event) => onEditorChange('name', event.target.value)}
                    placeholder="Weekly groceries"
                    className={inputClassName}
                  />
                </div>

                <div className="space-y-3">
                  <div>
                    <h2 className="m-0 text-lg font-semibold text-app-ink">Icon</h2>
                    <p className="mt-1 text-sm leading-6 text-app-muted">
                      Pick the symbol that will represent this collection across cards and detail views.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {editorIconChoices.map((icon) => {
                      const meta = getCollectionIconMeta(icon);
                      const isActive = listEditorForm.icon === icon;

                      return (
                        <button
                          key={icon}
                          type="button"
                          className={cn(
                            'flex items-center gap-3 rounded-2xl border p-3 text-left transition',
                            isActive
                              ? 'border-app-accent/40 bg-blue-50 shadow-subtle'
                              : 'border-app-border/80 bg-app-surface hover:border-app-accent/20 hover:bg-app-surface-alt'
                          )}
                          onClick={() => onEditorChange('icon', icon)}
                        >
                          <span
                            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-subtle"
                            style={{ backgroundColor: listEditorForm.color || '#60a5fa' }}
                          >
                            <CollectionIcon icon={icon} size="md" />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-app-ink">{meta.label}</span>
                            <span className="block text-xs text-app-muted">Use on collection cards</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h2 className="m-0 text-lg font-semibold text-app-ink">Color theme</h2>
                    <p className="mt-1 text-sm leading-6 text-app-muted">
                      Apply a quick accent for the icon tile and visual grouping.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="List color theme">
                    {editorColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        role="radio"
                        aria-checked={listEditorForm.color === color}
                        className={cn(
                          'h-11 w-11 rounded-full border-4 transition',
                          listEditorForm.color === color
                            ? 'border-app-surface shadow-panel'
                            : 'border-transparent shadow-subtle'
                        )}
                        style={{ backgroundColor: color }}
                        onClick={() => onEditorChange('color', color)}
                      >
                        <span className="sr-only">{color}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className={sectionClassName}>
              <SectionTitle
                title={isEditing ? 'Collection scope' : 'Scope'}
                copy={
                  isEditing
                    ? 'Scope remains fixed after creation so existing links and permissions stay stable.'
                    : 'Choose whether this collection belongs to you or your family workspace.'
                }
                action={<ScopeBadge scope={listEditorForm.scope} />}
              />

              {isEditing ? (
                <div className="mt-5 rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                  <p className="m-0 text-sm font-semibold text-app-ink">{scopeLabel(listEditorForm.scope)}</p>
                  <p className="mt-1 text-sm leading-6 text-app-muted">
                    {isFamilyScope
                      ? 'Anyone in your family group can continue editing this collection.'
                      : 'This collection stays inside your personal workspace.'}
                  </p>
                </div>
              ) : (
                <div className="mt-5">
                  <SegmentedTabs
                    value={listEditorForm.scope}
                    onValueChange={(value) => onEditorChange('scope', value)}
                    ariaLabel="Collection scope"
                    items={[
                      { value: 'personal', label: 'Personal' },
                      { value: 'family', label: 'Family shared', disabled: !family },
                    ]}
                  />
                  {!family ? (
                    <p className="mt-3 text-sm leading-6 text-app-muted">
                      Open Family first to unlock household-shared collections.
                    </p>
                  ) : null}
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-8">
            <section className={cn(sectionClassName, 'bg-app-shell')}>
              <SectionTitle
                title="Preview"
                copy="This matches the new card style before you save."
              />
              <div className="mt-6 rounded-[24px] border border-app-border/70 bg-app-surface/95 p-6 shadow-panel">
                <div className="flex items-start justify-between gap-4">
                  <div
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-subtle"
                    style={{ backgroundColor: listEditorForm.color || '#60a5fa' }}
                  >
                    <CollectionIcon icon={listEditorForm.icon} size="lg" />
                  </div>
                  <ScopeBadge scope={listEditorForm.scope} />
                </div>
                <p className="mt-5 text-xl font-semibold tracking-tight text-app-ink">{previewName}</p>
                <p className="mt-2 text-sm leading-6 text-app-muted">
                  {isFamilyScope
                    ? 'Shared collection for the whole household.'
                    : 'Private collection in your personal workspace.'}
                </p>
              </div>
            </section>

            {listEditorStatus.message ? (
              <section className={sectionClassName}>
                <StatusPill tone={toneForStatus(listEditorStatus.type)}>
                  {listEditorStatus.type}
                </StatusPill>
                <p className="mt-3 text-sm leading-6 text-app-muted">{listEditorStatus.message}</p>
              </section>
            ) : null}

            {isEditing ? (
              <section className={sectionClassName}>
                <SectionTitle
                  title="Danger zone"
                  copy="Archive to hide the list or delete it permanently."
                />
                <div className="mt-5 flex flex-col gap-3">
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                    type="button"
                    onClick={onArchiveList}
                  >
                    <Layers3 className="h-4 w-4" aria-hidden="true" />
                    <span>Archive list</span>
                  </button>
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 transition hover:bg-red-100"
                    type="button"
                    onClick={onDeleteList}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    <span>Delete list</span>
                  </button>
                </div>
              </section>
            ) : null}

            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-app-accent px-5 py-4 text-sm font-semibold text-white shadow-panel transition hover:bg-app-accent-strong"
              type="button"
              onClick={onSaveList}
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span>{isEditing ? 'Save changes' : 'Create list'}</span>
            </button>
          </aside>
        </div>
      </section>
    );
  }

  if (mode === 'detail') {
    const listLabel = toDisplayText(activeList?.name ?? activeList?.title, 'Collection detail');
    const listDescription = toDisplayText(
      activeList?.description ?? activeList?.summary ?? activeList?.notes,
      'Manage items, switch between open and completed work, and keep the collection aligned with the rest of the household.'
    );

    return (
      <section className="w-full max-w-[1380px] text-app-ink">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_280px]">
          <section className={cn(sectionClassName, 'bg-app-shell')}>
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <div
                  className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-subtle"
                  style={{ backgroundColor: activeList?.color || '#60a5fa' }}
                >
                  <CollectionIcon icon={activeList?.icon} size="lg" />
                </div>
                <div>
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-app-accent-strong">
                    {scopeLabel(activeScope)}
                  </p>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-app-ink">{listLabel}</h1>
                  <p className="mt-2 max-w-2xl text-sm leading-7 text-app-muted">{listDescription}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-2.5 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                  type="button"
                  onClick={onBackToCollections}
                >
                  Back to collections
                </button>
                <button
                  className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-2.5 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                  type="button"
                  onClick={onOpenListEditor}
                >
                  <Edit3 className="h-4 w-4" aria-hidden="true" />
                  <span>Edit list</span>
                </button>
              </div>
            </div>
          </section>

          <aside className={sectionClassName}>
            <StatusPill tone={toneForStatus(listsStatus.type)}>{listsStatus.type}</StatusPill>
            <p className="mt-3 text-sm leading-6 text-app-muted">{listsStatus.message}</p>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
              Signed in as
            </p>
            <p className="mt-1 text-sm font-medium text-app-ink">{currentUser?.email || 'Unknown'}</p>
          </aside>
        </div>

        <div className="mt-8 grid gap-8 xl:grid-cols-[320px_minmax(0,1.25fr)]">
          <aside className="space-y-8">
            <section className={sectionClassName}>
              <SectionTitle
                title="Collection details"
                copy="Quick metadata and filter controls without changing existing list behavior."
              />
              <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">Scope</p>
                  <p className="mt-2 text-lg font-semibold text-app-ink">{scopeLabel(activeScope)}</p>
                </div>
                <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">Filter</p>
                  <p className="mt-2 text-lg font-semibold capitalize text-app-ink">{activeFilter}</p>
                </div>
                <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">Items</p>
                  <p className="mt-2 text-lg font-semibold text-app-ink">{activeListItems.length}</p>
                </div>
                <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">Icon</p>
                  <p className="mt-2 text-lg font-semibold text-app-ink">
                    {getCollectionIconMeta(activeList?.icon).label}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <SegmentedTabs
                  value={activeFilter}
                  onValueChange={onListFilterChange}
                  ariaLabel="Collection item filter"
                  items={[
                    { value: 'open', label: 'Open' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'all', label: 'All' },
                  ]}
                />
              </div>
            </section>

            <section className={sectionClassName}>
              <SectionTitle
                title={editingListItemId ? 'Edit item' : 'Add item'}
                copy={
                  editingListItemId
                    ? 'Update the selected item without leaving the collection detail view.'
                    : 'Quickly add the next checklist item to this collection.'
                }
              />
              <form className="mt-6 space-y-5" onSubmit={onAddListItem}>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="collection-item-title">
                    Title
                  </label>
                  <input
                    id="collection-item-title"
                    value={listItemDraft.title}
                    onChange={(event) => onListItemDraftChange('title', event.target.value)}
                    placeholder="Milk"
                    className={inputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="collection-item-notes">
                    Notes
                  </label>
                  <textarea
                    id="collection-item-notes"
                    rows="4"
                    value={listItemDraft.notes}
                    onChange={(event) => onListItemDraftChange('notes', event.target.value)}
                    placeholder="Optional context"
                    className={inputClassName}
                  />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="collection-item-due-date">
                      Due date
                    </label>
                    <input
                      id="collection-item-due-date"
                      type="datetime-local"
                      value={listItemDraft.dueDate}
                      onChange={(event) => onListItemDraftChange('dueDate', event.target.value)}
                      className={inputClassName}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-app-ink" htmlFor="collection-item-priority">
                      Priority
                    </label>
                    <select
                      id="collection-item-priority"
                      value={listItemDraft.priority}
                      onChange={(event) => onListItemDraftChange('priority', event.target.value)}
                      className={inputClassName}
                    >
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                      <option value="urgent">urgent</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink">Recurrence</label>
                  <RecurrenceEditor
                    value={listItemDraft.recurrence}
                    onChange={(value) => onListItemDraftChange('recurrence', value)}
                    idPrefix="collection-item-recurrence"
                    requireDateCopy="A recurring collection item needs a due date. Completing it advances to the next occurrence."
                  />
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white shadow-subtle transition hover:bg-app-accent-strong"
                    type="submit"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    <span>{editingListItemId ? 'Save item' : 'Add item'}</span>
                  </button>
                  {editingListItemId ? (
                    <button
                      className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                      type="button"
                      onClick={onCancelListItemEdit}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </form>
            </section>
          </aside>

          <section className={sectionClassName}>
            <SectionTitle
              title="Items"
              copy={`${scopeLabel(activeScope)} collection detail.`}
              action={<StatusPill tone="border-app-border/80 bg-app-surface-alt text-app-ink">{itemsPagination.totalItems} loaded</StatusPill>}
            />

            {itemsPagination.totalItems ? (
              <div className="mt-6">
                <div className="space-y-5">
                  {itemsPagination.paginatedItems.map((item) => (
                    <ItemRow
                      item={item}
                      isEditing={editingListItemId === getItemId(item)}
                      key={getItemId(item)}
                      onToggleStatus={onToggleListItemStatus}
                      onEdit={onEditListItem}
                      onDelete={onDeleteListItem}
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={itemsPagination.currentPage}
                  totalPages={itemsPagination.totalPages}
                  hasNextPage={itemsPagination.hasNextPage}
                  hasPreviousPage={itemsPagination.hasPreviousPage}
                  onGoToPage={itemsPagination.goToPage}
                  onGoToNext={itemsPagination.goToNextPage}
                  onGoToPrevious={itemsPagination.goToPreviousPage}
                  onGoToFirst={itemsPagination.goToFirstPage}
                  onGoToLast={itemsPagination.goToLastPage}
                  startIndex={itemsPagination.startIndex}
                  endIndex={itemsPagination.endIndex}
                  totalItems={itemsPagination.totalItems}
                />
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  title="No items in this view."
                  copy="Add a new item or switch the current filter."
                />
              </div>
            )}
          </section>
        </div>
      </section>
    );
  }

  const personalCount = allLists.filter((list) => list.scope !== 'family').length;
  const familyCount = allLists.filter((list) => list.scope === 'family').length;

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <CollectionHeroCard
        currentUser={currentUser}
        scopeFilter={scopeFilter}
        onScopeFilterChange={onScopeFilterChange}
        personalCount={personalCount}
        familyCount={familyCount}
        visibleCount={allListsPagination.totalItems}
      />

      <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className={sectionClassName}>
          <SectionTitle
            title="Collections"
            copy={
              scopeFilter !== 'all'
                ? `Showing ${scopeFilter} collections`
                : 'All personal and family collections in one grid'
            }
            action={<StatusPill tone={toneForStatus(listsStatus.type)}>{listsStatus.message}</StatusPill>}
          />

          {allListsPagination.totalItems ? (
            <div className="mt-6">
              <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3">
                {allListsPagination.paginatedItems.map((list) => (
                  <ListCard key={getListId(list)} list={list} onOpen={onOpenList} />
                ))}
              </div>
              <Pagination
                currentPage={allListsPagination.currentPage}
                totalPages={allListsPagination.totalPages}
                hasNextPage={allListsPagination.hasNextPage}
                hasPreviousPage={allListsPagination.hasPreviousPage}
                onGoToPage={allListsPagination.goToPage}
                onGoToNext={allListsPagination.goToNextPage}
                onGoToPrevious={allListsPagination.goToPreviousPage}
                onGoToFirst={allListsPagination.goToFirstPage}
                onGoToLast={allListsPagination.goToLastPage}
                startIndex={allListsPagination.startIndex}
                endIndex={allListsPagination.endIndex}
                totalItems={allListsPagination.totalItems}
              />
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                title="No collections matched this filter."
                copy="Switch the scope filter or create a new collection from the side panel."
              />
            </div>
          )}
        </section>

        <aside className="space-y-8">
          <section className={cn(sectionClassName, 'bg-app-shell')}>
            <SectionTitle
              title="Create collection"
              copy="Start a new personal or family shared list without changing any backend behavior."
            />
            {!family ? (
              <p className="mt-4 text-sm leading-6 text-app-muted">
                Open Family to create a shared household and unlock family collections.
              </p>
            ) : null}
            <button
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-app-accent px-5 py-4 text-sm font-semibold text-white shadow-panel transition hover:bg-app-accent-strong"
              type="button"
              onClick={onOpenListEditor}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span>Create list</span>
            </button>
          </section>

          <section className={cn(sectionClassName, 'border-dashed bg-transparent shadow-none')}>
            <SectionTitle
              title="Workspace status"
              copy="The current list state stays identical; only the presentation changed."
            />
            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Signed in
                </p>
                <p className="mt-2 text-sm font-medium text-app-ink">{currentUser?.email || 'Unknown'}</p>
              </div>
              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Status
                </p>
                <p className="mt-2 text-sm leading-6 text-app-muted">{listsStatus.message}</p>
              </div>
              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Shared workspace
                </p>
                <p className="mt-2 text-sm leading-6 text-app-muted">
                  {family
                    ? 'Family collections are available in this workspace.'
                    : 'Family sharing is not set up yet.'}
                </p>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

export default ListsView;
